import React, { Fragment } from 'react';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import Bone from '@lightspeed/cirrus-bone';
import Group from '@lightspeed/cirrus-group';
import { IconArrowRight } from '@lightspeed/flame/Icon/ArrowRight';
import Text from '@lightspeed/cirrus-text';
import { spacing } from '@lightspeed/cirrus-tokens/utils';

import { getTimeOffset, offsetDate, DEFAULT_MIN_DAY, DEFAULT_MAX_DAY } from 'pages/utils/time';
import { DatePicker } from 'pages/components/DateRangePicker';
import LocalizeContext from 'pages/context/LocalizeContext';

import VendorReturnStatus from './VendorReturnStatus';

import {
  DetailsContainer,
  StatusCreatedByContainer,
  ShopToVendorContainer,
  DatesContainer,
  DateSentContainer,
  DateClosedContainer,
  breakWord,
} from './VendorReturnDetails.style';

const VendorReturnDetails = ({
  dateFormat,
  employee,
  enabledFields,
  shop,
  vendor,
  vendorReturn,
  isArchived,
  isLoading,
  isSubmitting,
  onClosedDateChange,
  onSentDateChange,
  /* formik */
  values,
}) =>
  !isEmpty(values) && !isLoading ? (
    <DetailsContainer>
      <ShopToVendorContainer>
        <div className="cr-mr-3">
          <Text heading4 className="cr-pb-3">
            From shop
          </Text>
          <Text body element="div" data-test="vendor-return-from-shop" css={breakWord}>
            <var>{shop.name}</var>
          </Text>
        </div>
        <IconArrowRight size="1rem" />
        <div className="cr-ml-3">
          <Text heading4 className="cr-pb-3">
            To vendor
          </Text>
          <Group>
            <Text body element="div" data-test="vendor-return-to-vendor" css={breakWord}>
              {vendor.name ? <var>{vendor.name}</var> : 'No vendor selected'}
            </Text>
          </Group>
        </div>
      </ShopToVendorContainer>
      <StatusCreatedByContainer>
        <div>
          <Text heading4 className="cr-pb-3">
            Status
          </Text>
          <VendorReturnStatus status={vendorReturn.status} data-test="vendor-return-status" />
        </div>
        <div className="cr-ml-3 cr-pl-3 cr-border-gray-100 cr-border-left-1">
          <Text heading4 className="cr-pb-3">
            Created by
          </Text>
          <Text body element="div" data-test="vendor-return-created-by" css={breakWord}>
            <var>
              {employee.firstName} {employee.lastName}
            </var>
          </Text>
        </div>
      </StatusCreatedByContainer>
      {(!!~enabledFields.indexOf('sentDate') ||
        !!~enabledFields.indexOf('closedDate') ||
        isArchived) && (
        <DatesContainer>
          {(!!~enabledFields.indexOf('sentDate') || isArchived) &&
            vendorReturn.status !== 'open' &&
            values.sentDate && (
              <DateSentContainer>
                <Text heading4 className="cr-pb-2">
                  Date Sent
                </Text>
                {!isArchived ? (
                  <LocalizeContext.Consumer>
                    {({ language }) => (
                      <DatePicker
                        key={language}
                        placement="bottom-end"
                        dateFormat={dateFormat}
                        date={offsetDate({
                          date: values.sentDate,
                          timezone: shop.timeZone,
                          keepTime: true,
                        })}
                        availableStartDate={offsetDate({
                          date: DEFAULT_MIN_DAY,
                          timezone: shop.timeZone,
                          keepTime: true,
                        })}
                        availableEndDate={offsetDate({
                          date: DEFAULT_MAX_DAY,
                          timezone: shop.timeZone,
                          keepTime: true,
                        })}
                        numberOfMonths={1}
                        onChange={onSentDateChange}
                        disabled={isSubmitting || isArchived}
                        offset={getTimeOffset(shop.timeZone)}
                      />
                    )}
                  </LocalizeContext.Consumer>
                ) : (
                  values.sentDate && (
                    <Text body element="div" data-test="vendor-return-closed-date">
                      <var>{moment(values.sentDate).format(dateFormat)}</var>
                    </Text>
                  )
                )}
              </DateSentContainer>
            )}
          {(!!~enabledFields.indexOf('closedDate') || isArchived) &&
            vendorReturn.status === 'done' &&
            values.closedDate && (
              <DateClosedContainer>
                <Text heading4 className="cr-pb-2">
                  Date Closed
                </Text>
                {!isArchived ? (
                  <LocalizeContext.Consumer>
                    {({ language }) => (
                      <Fragment>
                        <DatePicker
                          key={language}
                          placement="bottom-end"
                          dateFormat={dateFormat}
                          date={offsetDate({
                            date: values.closedDate,
                            timezone: shop.timeZone,
                            keepTime: true,
                          })}
                          availableStartDate={offsetDate({
                            date: DEFAULT_MIN_DAY,
                            timezone: shop.timeZone,
                            keepTime: true,
                          })}
                          availableEndDate={offsetDate({
                            date: DEFAULT_MAX_DAY,
                            timezone: shop.timeZone,
                            keepTime: true,
                          })}
                          numberOfMonths={1}
                          onChange={onClosedDateChange}
                          disabled={isSubmitting || isArchived}
                          offset={getTimeOffset(shop.timeZone)}
                        />
                      </Fragment>
                    )}
                  </LocalizeContext.Consumer>
                ) : (
                  values.closedDate && (
                    <Text body element="div" data-test="vendor-return-closed-date">
                      <var>{moment(values.closedDate).format(dateFormat)}</var>
                    </Text>
                  )
                )}
              </DateClosedContainer>
            )}
        </DatesContainer>
      )}
    </DetailsContainer>
  ) : (
    <VendorReturnDetailsSkeleton />
  );

export const VendorReturnDetailsSkeleton = () => (
  <DetailsContainer>
    <ShopToVendorContainer>
      <div className="cr-mr-4">
        <Text heading4 className="cr-pb-3">
          From shop
        </Text>
        <Text body>
          <Bone height={spacing(4)} />
        </Text>
      </div>
      <IconArrowRight size="1rem" />
      <div className="cr-ml-4">
        <Text heading4 className="cr-pb-3">
          To vendor
        </Text>
        <Text body element="span">
          <Bone height={spacing(4)} />
        </Text>
      </div>
    </ShopToVendorContainer>
    <StatusCreatedByContainer>
      <div className="cr-pr-3 cr-mr-3 cr-border-gray-100 cr-border-right-1">
        <Text heading4 className="cr-pb-3">
          Status
        </Text>
        <Bone height={spacing(4)} />
      </div>
      <div>
        <Text heading4 className="cr-pb-3">
          Created by
        </Text>
        <Text body>
          <Bone height={spacing(4)} />
        </Text>
      </div>
    </StatusCreatedByContainer>
  </DetailsContainer>
);

export default VendorReturnDetails;
